import React, { useState, useRef, useMemo, useEffect } from "react";
import "./Signup.css";
import Image from "../../../../assets/gif/signup.gif";
import { useNavigate } from "react-router-dom";
import axios from "axios";

/* ─── constants ─────────────────────────────────────────────── */
const STEPS = ["Account", "Education", "Preferences"];

const COUNTRY_CODES = [
  { code: "+91", flag: "🇮🇳", name: "India", iso: "IN" },
  { code: "+1", flag: "🇺🇸", name: "USA", iso: "US" },
  { code: "+44", flag: "🇬🇧", name: "United Kingdom", iso: "GB" },
  { code: "+61", flag: "🇦🇺", name: "Australia", iso: "AU" },
  { code: "+1", flag: "🇨🇦", name: "Canada", iso: "CA" },
  { code: "+971", flag: "🇦🇪", name: "UAE", iso: "AE" },
  { code: "+65", flag: "🇸🇬", name: "Singapore", iso: "SG" },
  { code: "+60", flag: "🇲🇾", name: "Malaysia", iso: "MY" },
  { code: "+49", flag: "🇩🇪", name: "Germany", iso: "DE" },
  { code: "+33", flag: "🇫🇷", name: "France", iso: "FR" },
  { code: "+81", flag: "🇯🇵", name: "Japan", iso: "JP" },
  { code: "+82", flag: "🇰🇷", name: "South Korea", iso: "KR" },
  { code: "+86", flag: "🇨🇳", name: "China", iso: "CN" },
  { code: "+55", flag: "🇧🇷", name: "Brazil", iso: "BR" },
  { code: "+92", flag: "🇵🇰", name: "Pakistan", iso: "PK" },
  { code: "+880", flag: "🇧🇩", name: "Bangladesh", iso: "BD" },
  { code: "+94", flag: "🇱🇰", name: "Sri Lanka", iso: "LK" },
  { code: "+977", flag: "🇳🇵", name: "Nepal", iso: "NP" },
];

const EDUCATION_MAP = {
  "Doctorate / PhD": {
    courses: ["PhD (Science)", "PhD (Engineering)", "PhD (Arts & Humanities)", "PhD (Commerce & Management)", "PhD (Law)", "PhD (Medicine)", "PhD (Education)", "Other"],
  },
  "Masters / Post-Graduation": {
    courses: ["MBA", "MCA", "M.Tech", "M.Sc", "M.Com", "MA", "LLM", "M.Ed", "Other"],
  },
  "Graduation / Diploma": {
    courses: ["B.Tech / B.E.", "BCA", "B.Sc", "B.Com", "BA", "BBA", "LLB", "MBBS", "B.Ed", "Diploma (Engineering)", "Diploma (Management)", "Other"],
  },
  "12th / Senior Secondary": {
    courses: ["Science (PCM)", "Science (PCB)", "Commerce", "Arts / Humanities", "Other"],
  },
  "10th / Secondary": {
    courses: ["CBSE", "ICSE", "State Board", "Other"],
  },
  "Below 10th": {
    courses: ["Primary School", "Middle School", "Other"],
  },
};

const DEGREE_OPTIONS = Object.keys(EDUCATION_MAP);

const SPECIALIZATION_MAP = {
  "B.Tech / B.E.": ["Computer Science & Engineering","Artificial Intelligence & Machine Learning","Data Science","Information Technology","Cyber Security","Electronics & Communication Engineering","Electrical Engineering","Mechanical Engineering","Civil Engineering","Chemical Engineering","Aerospace Engineering","Biotechnology Engineering","Robotics & Automation","Mechatronics Engineering","Environmental Engineering","Other"],
  "M.Tech": ["Computer Science & Engineering","Artificial Intelligence & Machine Learning","Data Science","Information Technology","Cyber Security","Electronics & Communication Engineering","Electrical Engineering","Mechanical Engineering","Civil Engineering","Chemical Engineering","Aerospace Engineering","Biotechnology Engineering","Robotics & Automation","Mechatronics Engineering","Environmental Engineering","Other"],
  "MCA": ["Software Engineering","Data Science","Artificial Intelligence","Machine Learning","Cyber Security","Cloud Computing","Full Stack Development","Mobile Application Development","Blockchain Technology","DevOps","Other"],
  "MBA": ["Finance","Marketing","Human Resources","Operations Management","Business Analytics","International Business","Supply Chain Management","Digital Marketing","Entrepreneurship","Information Technology Management","Other"],
  "BCA": ["Software Development","Data Science","Artificial Intelligence","Cyber Security","Cloud Computing","Web Development","Mobile App Development","Database Management","Networking","Game Development","Other"],
  "M.Sc": ["Physics","Chemistry","Mathematics","Computer Science","Biotechnology","Data Science","Environmental Science","Microbiology","Statistics","Electronics","Other"],
  "B.Sc": ["Physics","Chemistry","Mathematics","Computer Science","Biotechnology","Statistics","Microbiology","Information Technology","Environmental Science","Forensic Science","Other"],
  "MA": ["English Literature","History","Sociology","Economics","Political Science","Psychology","Public Administration","Philosophy","Journalism & Mass Communication","International Relations","Other"],
  "BA": ["English Literature","History","Sociology","Economics","Political Science","Psychology","Mass Communication","Public Administration","Geography","Fine Arts","Other"],
  "B.Com": ["Accounting","Finance","Banking & Insurance","Taxation","E-Commerce","Investment Management","Financial Markets","Corporate Accounting","Business Analytics","Auditing","Other"],
  "M.Com": ["Accounting","Finance","Banking","Taxation","Business Administration","Financial Management","International Business","Economics","Corporate Governance","Business Analytics","Other"],
  "BBA": ["Finance","Marketing","Human Resources","Entrepreneurship","International Business","Business Analytics","Digital Marketing","Operations Management","Supply Chain Management","Retail Management","Other"],
  "LLB": ["Criminal Law","Corporate Law","Constitutional Law","Family Law","Cyber Law","Intellectual Property Law","Taxation Law","Environmental Law","International Law","Labour Law","Other"],
  "LLM": ["Criminal Law","Corporate Law","International Law","Human Rights Law","Intellectual Property Rights","Constitutional Law","Environmental Law","Taxation Law","Commercial Law","Cyber Law","Other"],
  "MBBS": ["General Medicine","General Surgery","Paediatrics","Obstetrics & Gynaecology","Orthopaedics","Dermatology","Psychiatry","Radiology","Anaesthesiology","Cardiology","Other"],
  "B.Ed": ["Primary Education","Secondary Education","Special Education","Science Education","Mathematics Education","English Education","Social Science Education","Physical Education","Computer Education","Educational Technology","Other"],
  "M.Ed": ["Curriculum Design","Educational Psychology","Educational Technology","School Administration","Guidance & Counselling","Special Education","Higher Education","Educational Leadership","Inclusive Education","Assessment & Evaluation","Other"],
  "Diploma (Engineering)": ["Computer Engineering","Electronics Engineering","Mechanical Engineering","Civil Engineering","Electrical Engineering","Automobile Engineering","Mechatronics","Robotics","Chemical Engineering","Instrumentation Engineering","Other"],
  "Diploma (Management)": ["Business Management","Marketing","Human Resources","Finance","Digital Marketing","Retail Management","International Business","Supply Chain Management","Entrepreneurship","Operations Management","Other"],
};

const SKILLS_BY_COURSE = {
  "B.Tech / B.E.": {
    "Computer Science & Engineering": ["C/C++","Java","Python","Data Structures","Algorithms","Operating Systems","DBMS","Computer Networks","Web Development","Git","SQL","Machine Learning"],
    "Artificial Intelligence & Machine Learning": ["Python","Machine Learning","Deep Learning","TensorFlow","PyTorch","Natural Language Processing","Computer Vision","Data Preprocessing","Statistics","Model Deployment"],
    "Data Science": ["Python","R","SQL","Data Visualization","Pandas","NumPy","Machine Learning","Statistics","Power BI","Tableau"],
    "Information Technology": ["Java","Python","DBMS","Cloud Computing","Computer Networks","Cyber Security Basics","Web Development","Linux","Git","DevOps Fundamentals"],
    "Cyber Security": ["Network Security","Ethical Hacking","Penetration Testing","Cryptography","Kali Linux","Firewall Configuration","SIEM Tools","OWASP","Risk Assessment","Incident Response"],
    "Electronics & Communication Engineering": ["Circuit Design","VLSI","Embedded C","MATLAB","Signal Processing","PCB Design","Arduino","Raspberry Pi","Microcontrollers","Communication Systems"],
    "Electrical Engineering": ["Power Systems","Electrical Machines","MATLAB","PLC","SCADA","Control Systems","Circuit Analysis","Renewable Energy Systems","AutoCAD Electrical","ETAP"],
    "Mechanical Engineering": ["AutoCAD","SolidWorks","CATIA","MATLAB","Thermodynamics","Fluid Mechanics","Manufacturing Processes","CNC Programming","ANSYS","Robotics Basics"],
    "Civil Engineering": ["AutoCAD","STAAD Pro","Revit","Structural Analysis","Surveying","GIS","Project Management","Quantity Surveying","Construction Planning","ETABS"],
    "Chemical Engineering": ["Process Design","Chemical Reaction Engineering","Heat Transfer","Mass Transfer","Process Simulation","Aspen HYSYS","MATLAB","Plant Design","Quality Control","Safety Engineering"],
    "default": ["Technical Drawing","Problem Solving","MATLAB","Project Management","Communication Skills","Report Writing","Teamwork","MS Excel","Presentation Skills","Leadership"],
  },
  "MBA": {
    "Finance": ["Financial Modeling","Advanced Excel","Valuation","Financial Analysis","Investment Banking Basics","Risk Management","SAP FICO","Budgeting & Forecasting","Equity Research","Financial Planning"],
    "Marketing": ["Market Research","Brand Management","Consumer Behavior","Digital Marketing","SEO/SEM","Social Media Marketing","Google Analytics","CRM","Product Management","Advertising Strategy"],
    "Human Resources": ["Recruitment & Talent Acquisition","Payroll Management","HR Analytics","Performance Management","Compensation & Benefits","Labor Laws","Training & Development","Employee Engagement","HRMS","Conflict Resolution"],
    "Operations Management": ["Supply Chain Management","Logistics Management","Inventory Control","Lean Management","Six Sigma","Project Management","Quality Control","Procurement","ERP Systems","Process Optimization"],
    "Business Analytics": ["Advanced Excel","SQL","Power BI","Tableau","Python","R","Statistics","Data Visualization","Predictive Modeling","Machine Learning Basics"],
    "default": ["MS Office","Communication Skills","Leadership","Strategic Thinking","Problem Solving","Team Management","Presentation Skills","Business Communication","Time Management","Negotiation Skills"],
  },
  "MCA": {
    "Software Engineering": ["Java","Python","C++","Data Structures","Algorithms","DBMS","System Design","Software Testing","Git","REST APIs"],
    "Full Stack Development": ["HTML","CSS","JavaScript","React","Node.js","Express.js","MongoDB","MySQL","REST APIs","Git"],
    "Data Science": ["Python","R","SQL","Pandas","NumPy","Data Visualization","Machine Learning","Statistics","Power BI","Tableau"],
    "default": ["Java","Python","C/C++","DBMS","Data Structures","Web Development","React","Node.js","SQL","Git","Linux","REST APIs"],
  },
  "BCA": {
    "Web Development": ["HTML","CSS","JavaScript","React","Node.js","Express.js","MongoDB","MySQL","REST APIs","Bootstrap"],
    "default": ["HTML","CSS","JavaScript","Java","Python","SQL","Git","Problem Solving","Communication Skills","Teamwork"],
  },
  "B.Sc": {
    "Computer Science": ["C/C++","Java","Python","Data Structures","Algorithms","DBMS","Operating Systems","Computer Networks","Web Development","Software Engineering"],
    "default": ["Research Skills","Laboratory Techniques","Data Analysis","Report Writing","MS Office","Communication Skills","Teamwork","Problem Solving","Presentation Skills","Critical Thinking"],
  },
  "BA": {
    "Mass Communication": ["Content Writing","Video Editing","Photography","Social Media","Journalism","Adobe Premiere","Canva"],
    "Economics": ["Data Analysis","Excel","STATA","EViews","R","Economic Research"],
    default: ["Communication","Research","Content Writing","MS Office","Public Speaking"],
  },
  "B.Com": {
    default: ["Tally","Excel","GST","Accounting","Auditing","Taxation","SAP","Financial Statements","MS Office"],
  },
  default: ["Communication","MS Office","Problem Solving","Teamwork","Leadership","Time Management","Critical Thinking","Adaptability"],
};

const UNIVERSAL_SKILLS = [
  "Communication","Teamwork","Leadership","Problem Solving","Critical Thinking",
  "Time Management","MS Office","Excel","PowerPoint","Google Workspace",
  "Project Management","Adaptability","Creativity","Attention to Detail",
  "Customer Service","Data Analysis","Social Media","Content Writing",
  "English Proficiency","Presentation Skills",
];

const CITIES_BY_COUNTRY = {
  IN: { label: "🇮🇳 India", cities: ["Mumbai","Delhi","Bengaluru","Hyderabad","Chennai","Kolkata","Pune","Ahmedabad","Jaipur","Lucknow","Surat","Kanpur","Nagpur","Indore","Patna","Bhopal","Visakhapatnam","Vadodara","Coimbatore","Agra","Nashik","Faridabad","Meerut","Ghaziabad","Noida","Gurugram","Chandigarh","Kochi","Thiruvananthapuram","Bhubaneswar","Mysuru","Raipur","Ranchi","Guwahati","Amritsar","Jabalpur","Vijayawada","Srinagar","Jodhpur","Madurai","Varanasi","Rajkot","Aurangabad"] },
  US: { label: "🇺🇸 United States", cities: ["New York","Los Angeles","Chicago","Houston","Phoenix","Philadelphia","San Antonio","San Diego","Dallas","San Jose","Austin","Jacksonville","San Francisco","Columbus","Indianapolis","Seattle","Denver","Nashville","Boston","Portland","Las Vegas","Memphis","Atlanta","Miami","Minneapolis","Raleigh","Omaha","Cleveland","Tulsa","Arlington"] },
  GB: { label: "🇬🇧 United Kingdom", cities: ["London","Birmingham","Manchester","Leeds","Glasgow","Sheffield","Bradford","Edinburgh","Liverpool","Bristol","Cardiff","Leicester","Coventry","Nottingham","Newcastle","Southampton","Belfast","Brighton","Plymouth","Derby","Stoke-on-Trent","Wolverhampton","Oxford","Cambridge"] },
  AU: { label: "🇦🇺 Australia", cities: ["Sydney","Melbourne","Brisbane","Perth","Adelaide","Gold Coast","Newcastle","Canberra","Wollongong","Hobart","Geelong","Townsville","Cairns","Darwin","Toowoomba","Ballarat","Bendigo","Albury"] },
  CA: { label: "🇨🇦 Canada", cities: ["Toronto","Montreal","Vancouver","Calgary","Edmonton","Ottawa","Winnipeg","Quebec City","Hamilton","Kitchener","London","Victoria","Halifax","Oshawa","Windsor","Saskatoon","Regina","Sherbrooke"] },
  AE: { label: "🇦🇪 UAE", cities: ["Dubai","Abu Dhabi","Sharjah","Al Ain","Ajman","Ras Al Khaimah","Fujairah","Umm Al Quwain","Khor Fakkan","Kalba","Dibba Al Hisn"] },
  SG: { label: "🇸🇬 Singapore", cities: ["Singapore","Jurong East","Tampines","Woodlands","Ang Mo Kio","Bedok","Sengkang","Hougang","Bukit Batok","Choa Chu Kang"] },
  MY: { label: "🇲🇾 Malaysia", cities: ["Kuala Lumpur","George Town","Ipoh","Shah Alam","Petaling Jaya","Johor Bahru","Subang Jaya","Kota Kinabalu","Kuching","Seremban","Malacca City","Miri","Sandakan","Alor Setar","Sibu"] },
  DE: { label: "🇩🇪 Germany", cities: ["Berlin","Hamburg","Munich","Cologne","Frankfurt","Stuttgart","Düsseldorf","Leipzig","Dortmund","Essen","Bremen","Dresden","Hanover","Nuremberg","Duisburg","Bochum","Wuppertal","Bielefeld"] },
  FR: { label: "🇫🇷 France", cities: ["Paris","Marseille","Lyon","Toulouse","Nice","Nantes","Strasbourg","Montpellier","Bordeaux","Lille","Rennes","Reims","Le Havre","Saint-Étienne","Toulon","Grenoble","Dijon","Angers"] },
  JP: { label: "🇯🇵 Japan", cities: ["Tokyo","Yokohama","Osaka","Nagoya","Sapporo","Fukuoka","Kawasaki","Kobe","Kyoto","Saitama","Hiroshima","Sendai","Kitakyushu","Chiba","Setagaya","Asahikawa","Hamamatsu","Okayama"] },
  KR: { label: "🇰🇷 South Korea", cities: ["Seoul","Busan","Incheon","Daegu","Daejeon","Gwangju","Suwon","Ulsan","Changwon","Seongnam","Goyang","Yongin","Bucheon","Cheongju","Ansan","Jeonju","Cheonan","Namyangju"] },
  CN: { label: "🇨🇳 China", cities: ["Shanghai","Beijing","Guangzhou","Shenzhen","Chengdu","Tianjin","Wuhan","Xi'an","Hangzhou","Nanjing","Chongqing","Suzhou","Qingdao","Dongguan","Shenyang","Zhengzhou","Jinan","Foshan"] },
  BR: { label: "🇧🇷 Brazil", cities: ["São Paulo","Rio de Janeiro","Brasília","Salvador","Fortaleza","Belo Horizonte","Manaus","Curitiba","Recife","Porto Alegre","Belém","Goiânia","Guarulhos","Campinas","São Luís"] },
  PK: { label: "🇵🇰 Pakistan", cities: ["Karachi","Lahore","Islamabad","Rawalpindi","Faisalabad","Multan","Hyderabad","Peshawar","Quetta","Gujranwala","Sialkot","Bahawalpur","Sargodha","Sukkur","Larkana","Sheikhupura","Rahim Yar Khan"] },
  BD: { label: "🇧🇩 Bangladesh", cities: ["Dhaka","Chittagong","Sylhet","Rajshahi","Khulna","Barisal","Comilla","Mymensingh","Rangpur","Gazipur","Narayanganj","Tongi"] },
  LK: { label: "🇱🇰 Sri Lanka", cities: ["Colombo","Dehiwala","Moratuwa","Jaffna","Negombo","Kandy","Kotte","Galle","Trincomalee","Batticaloa","Matara","Puttalam"] },
  NP: { label: "🇳🇵 Nepal", cities: ["Kathmandu","Pokhara","Lalitpur","Bhaktapur","Biratnagar","Birgunj","Dharan","Bharatpur","Janakpur","Hetauda"] },
};

const REMOTE_OPTIONS = ["Remote / Work From Home", "Pan India", "Anywhere"];
const ALL_LOCATIONS = [...Object.values(CITIES_BY_COUNTRY).flatMap(c => c.cities), ...REMOTE_OPTIONS];

/* ─── Validation ─── */
const VALIDATORS = {
  firstName: v => {
    if (!v?.trim()) return "First name is required";
    if (!/^[a-zA-Z\s'-]{2,40}$/.test(v.trim())) return "Enter a valid first name (letters only, 2–40 chars)";
    return null;
  },
  lastName: v => {
    if (!v?.trim()) return "Last name is required";
    if (!/^[a-zA-Z\s'-]{1,40}$/.test(v.trim())) return "Enter a valid last name";
    return null;
  },
  email: v => {
    if (!v?.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim())) return "Enter a valid email address";
    return null;
  },
  password: v => {
    if (!v) return "Password is required";
    if (v.length < 6) return "Password must be at least 6 characters";
    if (!/[A-Z]/.test(v)) return "Include at least one uppercase letter";
    if (!/[0-9]/.test(v)) return "Include at least one number";
    return null;
  },
  mobile: (v, countryCode) => {
    if (!v?.trim()) return "Mobile number is required";
    const digits = v.replace(/\D/g, "");
    if (countryCode === "+91") {
      if (!/^[6-9]\d{9}$/.test(digits)) return "Enter a valid 10-digit Indian mobile number";
    } else {
      if (digits.length < 6 || digits.length > 15) return "Enter a valid mobile number (6–15 digits)";
    }
    return null;
  },
  workStatus: v => (!v ? "Please select your work status" : null),
};

/* ─── Username helpers ─── */
function generateUsername(firstName, lastName) {
  const f = (firstName || "").replace(/[^a-zA-Z]/g, "").toLowerCase().slice(0, 4);
  const l = (lastName || "").replace(/[^a-zA-Z]/g, "").toLowerCase().slice(0, 4);
  const suffix = Math.floor(1000 + Math.random() * 9000);
  return f + l + suffix;
}
function generateUsernameSuggestions(firstName, lastName, count = 3) {
  return Array.from({ length: count }, () => generateUsername(firstName, lastName));
}

/* ─── FloatingOrbs ─── */
function FloatingOrbs() {
  return (
    <div className="cs-orbs" aria-hidden="true">
      <div className="cs-orb cs-orb-1" />
      <div className="cs-orb cs-orb-2" />
      <div className="cs-orb cs-orb-3" />
      <div className="cs-orb cs-orb-4" />
    </div>
  );
}

/* ─── StepBar ─── */
function StepBar({ current }) {
  return (
    <div className="cs-step-bar">
      {STEPS.map((label, i) => (
        <div key={label} className={`cs-step-item ${i < current ? "done" : ""} ${i === current ? "active" : ""}`}>
          <div className="cs-step-circle">
            {i < current ? <span className="cs-step-check">✓</span> : <span>{i + 1}</span>}
          </div>
          <span className="cs-step-label">{label}</span>
          {i < STEPS.length - 1 && <div className={`cs-step-line ${i < current ? "done" : ""}`} />}
        </div>
      ))}
    </div>
  );
}

/* ─── Field ─── */
function Field({ label, required, children, error, delay = 0 }) {
  return (
    <div className="cs-field" style={{ animationDelay: `${delay}ms` }}>
      <label className="cs-field-label">
        {label}{required && <span className="cs-field-req">*</span>}
      </label>
      {children}
      {error && (
        <div className="cs-field-error">
          <span className="cs-err-icon">⚠</span> {error}
        </div>
      )}
    </div>
  );
}

/* ─── OptionPills ─── */
function OptionPills({ options, value, onChange, error, multi = false }) {
  const isSelected = (item) => multi ? (Array.isArray(value) ? value.includes(item) : false) : value === item;
  const handleClick = (item) => {
    if (multi) {
      const arr = Array.isArray(value) ? value : [];
      onChange(arr.includes(item) ? arr.filter(v => v !== item) : [...arr, item]);
    } else {
      onChange(item);
    }
  };
  return (
    <>
      <div className="cs-option-wrap">
        {options.map((item) => (
          <button key={item} type="button" className={`cs-option-pill ${isSelected(item) ? "active" : ""}`} onClick={() => handleClick(item)}>
            {isSelected(item) && <span className="cs-pill-check">✓ </span>}
            {item}
          </button>
        ))}
      </div>
      {error && <div className="cs-field-error"><span className="cs-err-icon">⚠</span> {error}</div>}
    </>
  );
}

/* ─── CountryCodeDropdown ─── */
function CountryCodeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef();
  const selected = COUNTRY_CODES.find(c => c.iso === value) || COUNTRY_CODES[0];
  const filtered = useMemo(() => COUNTRY_CODES.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.code.includes(search)), [search]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="cs-cc-wrap" ref={ref}>
      <button type="button" className="cs-cc-trigger" onClick={() => setOpen(o => !o)}>
        <span>{selected.flag}</span>
        <span className="cs-cc-code">{selected.code}</span>
        <span className="cs-cc-arrow">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="cs-cc-dropdown">
          <div className="cs-cc-search-wrap">
            <input autoFocus type="text" className="cs-cc-search" placeholder="Search country..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="cs-cc-list">
            {filtered.map((c, i) => (
              <button key={`${c.iso}-${i}`} type="button" className={`cs-cc-item ${selected.iso === c.iso ? "active" : ""}`} onClick={() => { onChange(c.iso); setOpen(false); setSearch(""); }}>
                <span className="cs-cc-item-flag">{c.flag}</span>
                <span className="cs-cc-item-name">{c.name}</span>
                <span className="cs-cc-item-code">{c.code}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── LocationAutocomplete ─── */
function LocationAutocomplete({ value, onChange, placeholder, countryIso = "IN" }) {
  const [inputVal, setInputVal] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const ref = useRef();
  const inputRef = useRef();
  const selected = Array.isArray(value) ? value : [];
  const countryData = CITIES_BY_COUNTRY[countryIso] || CITIES_BY_COUNTRY["IN"];

  const { countryCities, otherCities } = useMemo(() => {
    const countrySet = new Set(countryData.cities);
    return {
      countryCities: countryData.cities.filter(c => !selected.includes(c)),
      otherCities: ALL_LOCATIONS.filter(c => !countrySet.has(c) && !REMOTE_OPTIONS.includes(c) && !selected.includes(c)),
    };
  }, [countryIso, selected, countryData]);

  const { sections, hasResults } = useMemo(() => {
    const q = inputVal.trim().toLowerCase();
    if (!q) {
      const defaultCountry = countryCities.slice(0, 12);
      const remotes = REMOTE_OPTIONS.filter(r => !selected.includes(r));
      return {
        sections: [{ items: defaultCountry }, { label: "🌐 Remote / Flexible", items: remotes }].filter(s => s.items.length > 0),
        hasResults: defaultCountry.length > 0 || remotes.length > 0,
      };
    }
    const score = (city) => { const c = city.toLowerCase(); if (c === q) return 4; if (c.startsWith(q)) return 3; if (c.includes(` ${q}`)) return 2; if (c.includes(q)) return 1; return 0; };
    const matchingCountry = countryCities.map(c => ({ city: c, s: score(c) })).filter(x => x.s > 0).sort((a, b) => b.s - a.s).map(x => x.city).slice(0, 6);
    const matchingOther = otherCities.map(c => ({ city: c, s: score(c) })).filter(x => x.s > 0).sort((a, b) => b.s - a.s).map(x => x.city).slice(0, 4);
    const matchingRemote = REMOTE_OPTIONS.filter(r => !selected.includes(r) && r.toLowerCase().includes(q));
    return {
      sections: [
        matchingCountry.length > 0 && { label: `${countryData.label}`, items: matchingCountry },
        matchingOther.length > 0 && { label: "🌍 Other Cities", items: matchingOther },
        matchingRemote.length > 0 && { label: "🌐 Remote / Flexible", items: matchingRemote },
      ].filter(Boolean),
      hasResults: matchingCountry.length + matchingOther.length + matchingRemote.length > 0,
    };
  }, [inputVal, countryCities, otherCities, selected, countryData]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const addLocation = (loc) => { onChange([...selected, loc]); setInputVal(""); setOpen(true); };
  const removeLocation = (loc) => onChange(selected.filter(l => l !== loc));
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputVal.trim()) {
      const firstSuggestion = sections[0]?.items[0];
      addLocation(firstSuggestion || inputVal.trim());
      e.preventDefault();
    }
    if (e.key === "Backspace" && !inputVal && selected.length > 0) removeLocation(selected[selected.length - 1]);
    if (e.key === "Escape") setOpen(false);
  };
  const isRemoteOrHome = (loc) => loc.toLowerCase().includes("remote") || loc.toLowerCase().includes("home") || loc.toLowerCase().includes("flexible") || loc.toLowerCase().includes("anywhere");

  return (
    <div className="cs-loc-wrap" ref={ref}>
      <div className={`cs-loc-input-box ${focused ? "focused" : ""}`}>
        <span className="cs-loc-icon">📍</span>
        <div className="cs-loc-tags">
          {selected.map(loc => (
            <span key={loc} className="cs-loc-tag">
              <span className="cs-loc-tag-icon">{isRemoteOrHome(loc) ? "🏠" : "📍"}</span>
              {loc}
              <button type="button" onClick={() => removeLocation(loc)} className="cs-loc-tag-rm">×</button>
            </span>
          ))}
          <input ref={inputRef} type="text" value={inputVal} onChange={e => { setInputVal(e.target.value); setOpen(true); }} onFocus={() => { setFocused(true); setOpen(true); }} onBlur={() => setFocused(false)} onKeyDown={handleKeyDown} placeholder={selected.length === 0 ? (placeholder || "Search cities...") : "Add more..."} className="cs-loc-inp" />
        </div>
      </div>
      {open && (hasResults || inputVal.trim()) && (
        <div className="cs-loc-dropdown">
          {sections.map((section, si) => (
            <div key={si} className="cs-loc-section">
              <div className="cs-loc-section-label">{section.label}</div>
              {section.items.map(loc => (
                <button key={loc} type="button" className="cs-loc-item" onMouseDown={(e) => { e.preventDefault(); addLocation(loc); }}>
                  <span className="cs-loc-item-icon">{isRemoteOrHome(loc) ? "🏠" : "📍"}</span>
                  <span className="cs-loc-item-text">{loc}</span>
                  {inputVal.trim() && <span className="cs-loc-item-hint">{loc.toLowerCase().startsWith(inputVal.toLowerCase()) ? "↑ best match" : ""}</span>}
                </button>
              ))}
            </div>
          ))}
          {inputVal.trim() && !ALL_LOCATIONS.some(l => l.toLowerCase() === inputVal.trim().toLowerCase()) && (
            <div className="cs-loc-section">
              <button type="button" className="cs-loc-item cs-loc-custom" onMouseDown={(e) => { e.preventDefault(); addLocation(inputVal.trim()); }}>
                <span>➕</span>
                <span>Add "<strong>{inputVal}</strong>"</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── SkillsAutocomplete ─── */
function SkillsAutocomplete({ value, onChange, suggestedSkills = [] }) {
  const [inputVal, setInputVal] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const ref = useRef();
  const selected = Array.isArray(value) ? value : [];
  const allSkills = useMemo(() => [...new Set([...suggestedSkills, ...UNIVERSAL_SKILLS])], [suggestedSkills]);
  const filtered = useMemo(() => {
    const q = inputVal.toLowerCase();
    const base = q ? allSkills.filter(s => s.toLowerCase().includes(q) && !selected.includes(s)) : allSkills.filter(s => !selected.includes(s)).slice(0, 12);
    return base.slice(0, 10);
  }, [inputVal, selected, allSkills]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const addSkill = (skill) => { if (!selected.includes(skill)) onChange([...selected, skill]); setInputVal(""); };
  const removeSkill = (skill) => onChange(selected.filter(s => s !== skill));
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputVal.trim()) { addSkill(inputVal.trim()); e.preventDefault(); }
    if (e.key === "Backspace" && !inputVal && selected.length > 0) removeSkill(selected[selected.length - 1]);
  };
  const quickSuggestions = suggestedSkills.filter(s => !selected.includes(s)).slice(0, 6);

  return (
    <div className="cs-skills-wrap" ref={ref}>
      <div className={`cs-skills-input-box ${focused ? "focused" : ""}`}>
        <div className="cs-skills-tags">
          {selected.map(skill => (
            <span key={skill} className="cs-skill-tag">
              {skill}
              <button type="button" onClick={() => removeSkill(skill)} className="cs-skill-tag-rm">×</button>
            </span>
          ))}
          <input type="text" value={inputVal} onChange={e => { setInputVal(e.target.value); setOpen(true); }} onFocus={() => { setFocused(true); setOpen(true); }} onBlur={() => setFocused(false)} onKeyDown={handleKeyDown} placeholder={selected.length === 0 ? "Type or pick skills..." : "Add more skills..."} className="cs-skills-inp" />
        </div>
      </div>
      {quickSuggestions.length > 0 && (
        <div className="cs-skills-quick">
          <span className="cs-skills-quick-label">💡 Suggested:</span>
          {quickSuggestions.map(s => (
            <button key={s} type="button" className="cs-skill-suggest-pill" onMouseDown={() => addSkill(s)}>+ {s}</button>
          ))}
        </div>
      )}
      {open && filtered.length > 0 && (
        <div className="cs-skills-dropdown">
          {filtered.map(skill => (
            <button key={skill} type="button" className="cs-skill-item" onMouseDown={(e) => { e.preventDefault(); addSkill(skill); }}>
              <span className="cs-skill-item-icon">🔧</span>
              <span>{skill}</span>
            </button>
          ))}
          {inputVal.trim() && !allSkills.some(s => s.toLowerCase() === inputVal.toLowerCase()) && (
            <button type="button" className="cs-skill-item cs-skill-custom" onMouseDown={(e) => { e.preventDefault(); addSkill(inputVal.trim()); }}>
              <span>➕</span>
              <span>Add "{inputVal}"</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── PasswordStrength ─── */
function PasswordStrength({ password }) {
  const checks = [
    { label: "6+ chars", ok: password.length >= 6 },
    { label: "Uppercase", ok: /[A-Z]/.test(password) },
    { label: "Number", ok: /[0-9]/.test(password) },
    { label: "Symbol", ok: /[^A-Za-z0-9]/.test(password) },
  ];
  const score = checks.filter(c => c.ok).length;
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["", "#e74c3c", "#263012", "#f39c12", "#27ae60"];
  return (
    <div className="cs-pass-strength">
      <div className="cs-ps-bars">
        {[1, 2, 3, 4].map(i => (<div key={i} className="cs-ps-bar" style={{ background: i <= score ? colors[score] : undefined }} />))}
      </div>
      {score > 0 && <span className="cs-ps-label" style={{ color: colors[score] }}>{labels[score]}</span>}
      <div className="cs-ps-checks">
        {checks.map(c => (<span key={c.label} className={`cs-ps-check ${c.ok ? "ok" : ""}`}>{c.ok ? "✓" : "○"} {c.label}</span>))}
      </div>
    </div>
  );
}

/* ─── PhoneField ─── */
function PhoneField({ form, set, errors, touched, touch, mobileExists }) {
  const [focused, setFocused] = useState(false);
  /* FIX Bug 8: merge format error + duplicate error into Field's error prop */
  const fieldError = (touched.mobile && errors.mobile) || (mobileExists ? "This mobile number is already registered." : null);
  return (
    <Field label="Mobile Number" required error={fieldError} delay={200}>
      <div className={`cs-mobile-wrap ${focused ? "focused" : ""} ${fieldError ? "error" : ""} ${touched.mobile && !fieldError && form.mobile ? "valid" : ""}`}>
        <CountryCodeDropdown
          value={form.countryIso || "IN"}
          onChange={iso => {
            const cc = COUNTRY_CODES.find(c => c.iso === iso) || COUNTRY_CODES[0];
            set("countryIso", iso);
            set("countryCode", cc.code);
            set("mobile", "");
          }}
        />
        <input
          type="tel"
          placeholder={form.countryIso === "IN" ? "10-digit mobile number" : "Mobile number"}
          value={form.mobile || ""}
          onChange={e => { const val = e.target.value.replace(/[^\d\s+\-()]/g, ""); set("mobile", val); }}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); touch("mobile"); }}
          className="cs-inp-tel"
          maxLength={form.countryIso === "IN" ? 10 : 15}
        />
        {touched.mobile && !fieldError && form.mobile && <span className="cs-valid-tick">✓</span>}
      </div>
    </Field>
  );
}

/* ─── EducationEntry ─── */
function EducationEntry({ edu, index, onChange, onRemove, isOnly }) {
  const [focused, setFocused] = useState(null);
  const set = (key, val) => onChange({ ...edu, [key]: val });
  const selectedDegree = edu.degree;
  const courses = selectedDegree ? (EDUCATION_MAP[selectedDegree]?.courses || []) : [];
  const selectedCourse = edu.course;
  const showCustomCourse = selectedCourse === "Other";
  const specializations = selectedCourse && !showCustomCourse ? (SPECIALIZATION_MAP[selectedCourse] || []) : [];

  return (
    <div className="cs-edu-entry">
      <div className="cs-edu-entry-header">
        <div className="cs-edu-entry-num">Education #{index + 1}</div>
        {!isOnly && (<button type="button" className="cs-edu-remove-btn" onClick={onRemove}>✕ Remove</button>)}
      </div>
      <Field label="Qualification / Degree" delay={50}>
        <OptionPills options={DEGREE_OPTIONS} value={edu.degree} onChange={v => onChange({ ...edu, degree: v, course: "", customCourse: "", courseType: "", specialization: [], customSpecialization: "" })} />
      </Field>
      {selectedDegree && (
        <Field label="Course" delay={80}>
          <OptionPills options={courses} value={edu.course} onChange={v => onChange({ ...edu, course: v, customCourse: "", courseType: "", specialization: [], customSpecialization: "" })} />
        </Field>
      )}
      {showCustomCourse && (
        <Field label="Specify Your Course" delay={85}>
          <input type="text" placeholder="Enter your course name" value={edu.customCourse || ""} onChange={e => set("customCourse", e.target.value.replace(/[^a-zA-Z\s\-/().&]/g, ""))} onFocus={() => setFocused("customCourse")} onBlur={() => setFocused(null)} className={`cs-inp ${focused === "customCourse" ? "focused" : ""}`} />
        </Field>
      )}
      {selectedCourse && (
        <Field label="Course Type" delay={90}>
          <OptionPills options={["Full Time", "Part Time", "Distance / Correspondence"]} value={edu.courseType} onChange={v => set("courseType", v)} />
        </Field>
      )}
      {selectedCourse && specializations.length > 0 && !showCustomCourse && (
        <Field label="Specialization (can select multiple)" delay={100}>
          <OptionPills options={specializations} value={edu.specialization || []} onChange={v => set("specialization", v)} multi />
        </Field>
      )}
      {Array.isArray(edu.specialization) && edu.specialization.includes("Other") && (
        <Field label="Specify Your Specialization" delay={105}>
          <input type="text" placeholder="Enter your specialization" value={edu.customSpecialization || ""} onChange={e => set("customSpecialization", e.target.value.replace(/[^a-zA-Z\s\-/().&]/g, ""))} onFocus={() => setFocused("customSpecialization")} onBlur={() => setFocused(null)} className={`cs-inp ${focused === "customSpecialization" ? "focused" : ""}`} />
        </Field>
      )}
      {selectedCourse && (
        <>
          <Field label="University / Institute" delay={110}>
            <input type="text" placeholder="Enter university or institute name" value={edu.university || ""} onChange={e => set("university", e.target.value)} onFocus={() => setFocused("university")} onBlur={() => setFocused(null)} className={`cs-inp ${focused === "university" ? "focused" : ""}`} />
          </Field>
          <div className="cs-grid-2">
            <Field label="Starting Year" delay={120}>
              <input type="number" placeholder="e.g. 2021" value={edu.startYear || ""} onChange={e => set("startYear", e.target.value)} onFocus={() => setFocused(`sy-${index}`)} onBlur={() => setFocused(null)} className={`cs-inp ${focused === `sy-${index}` ? "focused" : ""}`} min={1950} max={2099} />
            </Field>
            <Field label="Passing Year" delay={130}>
              <input type="number" placeholder="e.g. 2025" value={edu.passingYear || ""} onChange={e => set("passingYear", e.target.value)} onFocus={() => setFocused(`py-${index}`)} onBlur={() => setFocused(null)} className={`cs-inp ${focused === `py-${index}` ? "focused" : ""}`} min={1950} max={2099} />
            </Field>
          </div>
          <Field label="Grading System" delay={140}>
            <div className="cs-sel-wrap">
              <select className="cs-sel" value={edu.gradingSystem || ""} onChange={e => set("gradingSystem", e.target.value)}>
                <option value="">Select grading system</option>
                <option value="scale10">Scale 10 (CGPA out of 10)</option>
                <option value="scale4">Scale 4 (GPA out of 4)</option>
                <option value="percentage">Percentage (out of 100)</option>
                <option value="pass">Course Requires a Pass</option>
              </select>
              <span className="cs-sel-arrow">▾</span>
            </div>
          </Field>
          {edu.gradingSystem && edu.gradingSystem !== "pass" && (
            <Field label={edu.gradingSystem === "scale10" ? "CGPA (out of 10)" : edu.gradingSystem === "scale4" ? "GPA (out of 4)" : "Percentage (out of 100)"} delay={150}>
              <input type="number" placeholder={edu.gradingSystem === "scale10" ? "e.g. 8.5" : edu.gradingSystem === "scale4" ? "e.g. 3.2" : "e.g. 75.5"} value={edu.percentage || ""} onChange={e => { const max = edu.gradingSystem === "scale10" ? 10 : edu.gradingSystem === "scale4" ? 4 : 100; const val = parseFloat(e.target.value); if (e.target.value === "" || (val >= 0 && val <= max)) set("percentage", e.target.value); }} onFocus={() => setFocused(`pct-${index}`)} onBlur={() => setFocused(null)} className={`cs-inp ${focused === `pct-${index}` ? "focused" : ""}`} step="0.01" />
            </Field>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Step 1: Account ─── */
function StepAccount({ form, set, errors, touched, touch, isGoogleSignup, emailExists, usernameInput, setUsernameInput, usernameAvailable, usernameSuggestions }) {
  const [showPass, setShowPass] = useState(false);
  const [focused, setFocused] = useState(null);

  const inp = (name, placeholder, type = "text") => (
    <input type={type} placeholder={placeholder} value={form[name] || ""}
      onChange={e => { let val = e.target.value; if (name === "firstName" || name === "lastName") val = val.replace(/[^a-zA-Z\s'-]/g, ""); set(name, val); }}
      onFocus={() => setFocused(name)} onBlur={() => { setFocused(null); touch(name); }}
      className={`cs-inp ${focused === name ? "focused" : ""} ${touched[name] && errors[name] ? "error" : ""} ${touched[name] && !errors[name] && form[name] ? "valid" : ""}`}
      autoComplete="off"
    />
  );

  return (
    <div className="cs-step-panel">
      <div className="cs-panel-intro">
        <div className="cs-panel-tag">STEP 01</div>
        <h2 className="cs-panel-title">Create Your <em>Account</em></h2>
        <p className="cs-panel-sub">Set up your TalentHive profile</p>
      </div>
      <div className="cs-required-note"><span className="cs-req-star">*</span> Fields marked with asterisk are required</div>

      <Field label="Profile Photo (optional)" delay={80}>
        <div className="cs-photo-upload">
          <label className="cs-photo-box">
            <input type="file" accept="image/*" hidden onChange={e => { const file = e.target.files[0]; if (file) set("profilePhoto", file); }} />
            {form.profilePhoto ? (
              <img src={URL.createObjectURL(form.profilePhoto)} alt="profile" className="cs-photo-preview" />
            ) : (
              <div className="cs-photo-placeholder"><span>📷</span><p>Upload photo</p></div>
            )}
          </label>
        </div>
      </Field>

      <div className="cs-grid-2">
        <Field label="First Name & Middle Name" required error={touched.firstName && errors.firstName} delay={50}>
          {inp("firstName", "Enter your first name & middle name")}
        </Field>
        <Field label="Last Name" required error={touched.lastName && errors.lastName} delay={50}>
          {inp("lastName", "Enter your last name")}
        </Field>
      </div>

      <Field label="Email" required error={touched.email && errors.email} delay={100}>
        <div className={`cs-inp-icon-wrap ${focused === "email" ? "focused" : ""} ${touched.email && errors.email ? "error" : ""} ${touched.email && !errors.email && form.email ? "valid" : ""}`}>
          <span className="cs-inp-icon">✉</span>
          <input type="email" placeholder="Enter your email address" value={form.email || ""} onChange={e => set("email", e.target.value.trim())} onFocus={() => setFocused("email")} onBlur={() => { setFocused(null); touch("email"); }} className="cs-inp-inner" autoComplete="off" />
          {touched.email && !errors.email && form.email && <span className="cs-valid-tick">✓</span>}
        </div>
        {emailExists && <div className="cs-field-error"><span className="cs-err-icon">⚠</span> This email is already registered.</div>}
      </Field>

      <Field label="Username" delay={120}>
        <div className={`cs-inp-icon-wrap ${usernameAvailable === true ? "valid" : usernameAvailable === false ? "error" : ""}`}>
          <span className="cs-inp-icon">@</span>
          <input className="cs-inp-inner" placeholder="Auto-generated or choose your own" value={usernameInput}
            onChange={e => { const val = e.target.value.replace(/[^a-zA-Z0-9_]/g, "").toLowerCase(); setUsernameInput(val); }} />
          {usernameAvailable === true && <span className="cs-valid-tick">✓</span>}
        </div>
        {usernameAvailable === false && (
          <>
            <div className="cs-field-error"><span className="cs-err-icon">⚠</span> Username taken</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
              {usernameSuggestions.map(s => (
                <button key={s} type="button" className="cs-skill-suggest-pill" onClick={() => setUsernameInput(s)}>{s}</button>
              ))}
            </div>
          </>
        )}
        {/* FIX Bug 6: corrected note — username CAN be changed once every 3 months */}
        <p style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4 }}>
          Lowercase letters, numbers, underscores only. Can be changed once every 3 months.
        </p>
      </Field>

      {!isGoogleSignup && (
        <Field label="Password" required error={touched.password && errors.password} delay={150}>
          <div className={`cs-inp-icon-wrap ${focused === "password" ? "focused" : ""} ${touched.password && errors.password ? "error" : ""} ${touched.password && !errors.password && form.password ? "valid" : ""}`}>
            <span className="cs-inp-icon">🔒</span>
            <input type={showPass ? "text" : "password"} placeholder="Min 6 chars, 1 uppercase, 1 number" value={form.password || ""} onChange={e => set("password", e.target.value)} onFocus={() => setFocused("password")} onBlur={() => { setFocused(null); touch("password"); }} className="cs-inp-inner" />
            <button type="button" className="cs-pass-toggle" onClick={() => setShowPass(s => !s)}>{showPass ? "🙈" : "👁"}</button>
          </div>
          {form.password && <PasswordStrength password={form.password} />}
        </Field>
      )}

      <Field label="Work Status" required error={touched.workStatus && errors.workStatus} delay={250}>
        <div className="cs-work-status-grid">
          {[{ val: "experienced", icon: "⚔️", title: "I'm Experienced" }, { val: "fresher", icon: "🛡️", title: "I'm a Fresher" }].map(w => (
            <div key={w.val} className={`cs-work-card ${form.workStatus === w.val ? "active" : ""}`} onClick={() => { set("workStatus", w.val); touch("workStatus"); }}>
              <div className="wc-icon">{w.icon}</div>
              <h4>{w.title}</h4>
              {form.workStatus === w.val && <div className="wc-selected-ring" />}
            </div>
          ))}
        </div>
        {touched.workStatus && errors.workStatus && <div className="cs-field-error"><span className="cs-err-icon">⚠</span> {errors.workStatus}</div>}
      </Field>
    </div>
  );
}

/* ─── Step 2: Education ─── */
function StepEducation({ form, set }) {
  const educations = form.educations || [{}];
  const updateEdu = (index, edu) => { const updated = [...educations]; updated[index] = edu; set("educations", updated); };
  const addEdu = () => set("educations", [...educations, {}]);
  const removeEdu = (index) => { const updated = educations.filter((_, i) => i !== index); set("educations", updated.length > 0 ? updated : [{}]); };

  const suggestedSkills = useMemo(() => {
    const skills = new Set();
    educations.forEach(edu => {
      const courseKey = edu.course;
      const specKey = Array.isArray(edu.specialization) ? edu.specialization[0] : edu.specialization;
      const courseData = SKILLS_BY_COURSE[courseKey] || SKILLS_BY_COURSE[edu.degree] || {};
      const skillList = courseData[specKey] || courseData.default || SKILLS_BY_COURSE.default;
      skillList.forEach(s => skills.add(s));
    });
    return [...skills];
  }, [educations]);

  return (
    <div className="cs-step-panel">
      <div className="cs-panel-intro">
        <div className="cs-panel-tag">STEP 02</div>
        <h2 className="cs-panel-title">Education <em>Details</em></h2>
        <p className="cs-panel-sub">Add one or multiple education entries</p>
      </div>
      <div className="cs-optional-note">Education details are optional but improve your chances of getting noticed. You can add multiple degrees.</div>
      {educations.map((edu, i) => (
        <EducationEntry key={i} edu={edu} index={i} onChange={(updated) => updateEdu(i, updated)} onRemove={() => removeEdu(i)} isOnly={educations.length === 1} />
      ))}
      <button type="button" className="cs-add-edu-btn" onClick={addEdu}><span>➕</span> Add Another Education</button>
      <div className="cs-edu-skills-section">
        <Field label="Key Skills" delay={160}>
          <SkillsAutocomplete value={form.skillsArray || []} onChange={v => set("skillsArray", v)} suggestedSkills={suggestedSkills} />
        </Field>
      </div>
    </div>
  );
}

/* ─── Step 3: Preferences ─── */
function StepPreferences({ form, set, errors, touched, touch, isGoogleSignup, mobileExists }) {
  const [focused, setFocused] = useState(null);
  return (
    <div className="cs-step-panel">
      <div className="cs-panel-intro">
        <div className="cs-panel-tag">STEP 03</div>
        <h2 className="cs-panel-title">Career <em>Preferences</em></h2>
        <p className="cs-panel-sub">Personalise your job matching experience</p>
      </div>
      {isGoogleSignup && (
        <div className="cs-google-phone-notice">
          <div className="cs-gpn-icon">📱</div>
          <div>
            <div className="cs-gpn-title">Mobile Number Required</div>
            <div className="cs-gpn-sub">Since you signed up with Google, please add your phone number to complete your profile.</div>
          </div>
        </div>
      )}
      <PhoneField form={form} set={set} errors={errors} touched={touched} touch={touch} mobileExists={mobileExists} />
      <Field label="Preferred Job Title / Headline" delay={50}>
        <input className={`cs-inp ${focused === "headline" ? "focused" : ""}`} placeholder='e.g. "Full Stack Developer | React Enthusiast"' value={form.headline || ""} onChange={e => set("headline", e.target.value)} onFocus={() => setFocused("headline")} onBlur={() => setFocused(null)} />
      </Field>
      <Field label="Preferred Locations" delay={100}>
        <LocationAutocomplete value={form.locationsArray || []} onChange={v => set("locationsArray", v)} placeholder="Search and add preferred cities..." countryIso={form.countryIso || "IN"} />
        <p className="cs-loc-country-hint">
          💡 City suggestions are based on your selected country code
          <span className="cs-loc-country-flag">{(CITIES_BY_COUNTRY[form.countryIso || "IN"] || CITIES_BY_COUNTRY["IN"]).label.split(" ")[0]}</span>
        </p>
      </Field>
      <Field label="Expected Salary (per annum)" delay={150}>
        <div className={`cs-inp-icon-wrap ${focused === "salary" ? "focused" : ""}`}>
          <span className="cs-inp-icon">₹</span>
          <input className="cs-inp-inner" placeholder="e.g. 6,00,000" value={form.salary || ""} onChange={e => { const val = e.target.value.replace(/[^\d,]/g, ""); set("salary", val); }} onFocus={() => setFocused("salary")} onBlur={() => setFocused(null)} />
        </div>
      </Field>
      <Field label="Gender" delay={200}>
        <OptionPills options={["Male", "Female", "Non-binary", "Prefer not to say"]} value={form.gender} onChange={v => set("gender", v)} />
      </Field>
    </div>
  );
}

/* ─── Success Screen ─── */
function SuccessScreen({ onReset, createdUsername }) {
  const navigate = useNavigate();
  return (
    <div className="cs-success-screen">
      <FloatingOrbs />
      <div className="cs-success-card">
        <div className="cs-success-ring"><div className="cs-success-icon">✓</div></div>
        <div className="cs-level-badge">LEVEL UP! <img src={Image} alt="image" className="gif-icon" /></div>
        <h2 className="cs-success-title">You're In!</h2>
        <p className="cs-success-sub">Your TalentHive account is live. Recruiters can discover your profile within <strong>24 hours</strong>.</p>
        <div className="cs-xp-bar-wrap">
          <div className="cs-xp-label">Profile XP</div>
          <div className="cs-xp-track"><div className="cs-xp-fill" /></div>
        </div>
        <div className="cs-success-steps">
          {["Account activated", "Education logged", "Preferences saved"].map((s, i) => (
            <div key={i} className="cs-success-step" style={{ animationDelay: `${300 + i * 140}ms` }}>
              <span className="cs-ss-dot">✓</span> {s}
            </div>
          ))}
        </div>
        {createdUsername && (
          <div style={{ background: "#fff8ee", border: "1px solid #263012", borderRadius: 10, padding: "12px 18px", margin: "16px 0", textAlign: "center" }}>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Your username</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#263012" }}>@{createdUsername}</div>
          </div>
        )}
        <div className="cs-success-actions g-5">
          <button className="cs-btn-primary" onClick={() => navigate("/employee/profile")}>View My Profile →</button>
          <button className="cs-btn-secondary" onClick={() => navigate("/employee/jobs")}>Browse Jobs</button>
        </div>
        <p className="referral-line">💛 Help someone grow — <a href="/referral">Invite a friend</a></p>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export default function CandidateSignup() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ countryIso: "IN", countryCode: "+91", educations: [{}], password: "" });
  const [submitted, setSubmitted] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isGoogleSignup, setIsGoogleSignup] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [mobileExists, setMobileExists] = useState(false);
  const [createdUsername, setCreatedUsername] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const [googleError, setGoogleError] = useState("");
  const cardRef = useRef();
  const navigate = useNavigate();

  const setField = (name, value) => setForm(f => ({ ...f, [name]: value }));
  const touch = (name) => setTouched(t => ({ ...t, [name]: true }));

  const errors = {
    firstName: VALIDATORS.firstName(form.firstName),
    lastName: VALIDATORS.lastName(form.lastName),
    email: VALIDATORS.email(form.email),
    password: isGoogleSignup ? null : VALIDATORS.password(form.password),
    mobile: VALIDATORS.mobile(form.mobile, form.countryCode || "+91"),
    workStatus: VALIDATORS.workStatus(form.workStatus),
  };

  /* FIX Bug 5: also block if username is taken */
  const step0Valid = !errors.firstName && !errors.lastName && !errors.email &&
    !errors.password && !errors.workStatus && !emailExists &&
    usernameAvailable !== false;

  const step2Valid = !errors.mobile && !mobileExists;

  const touchAllStep0 = () => setTouched(t => ({ ...t, firstName: true, lastName: true, email: true, password: true, workStatus: true }));
  const touchAllStep2 = () => setTouched(t => ({ ...t, mobile: true }));

  const transition = (dir) => {
    if (dir === 1 && step === 0 && !step0Valid) { touchAllStep0(); setSubmitAttempted(true); return; }
    setExiting(true);
    setTimeout(() => { setStep(s => s + dir); setExiting(false); cardRef.current?.scrollTo({ top: 0, behavior: "smooth" }); }, 260);
  };

  /* FIX Bug 2: upload photo after signup */
  const handleSubmit = async () => {
    if (!step2Valid) { touchAllStep2(); setSubmitAttempted(true); return; }
    try {
      const { data } = await axios.post("/api/candidate/signup", {
        firstName: form.firstName, lastName: form.lastName,
        email: form.email, password: form.password,
        countryCode: form.countryCode, countryIso: form.countryIso,
        mobile: form.mobile, workStatus: form.workStatus,
        headline: form.headline, gender: form.gender, salary: form.salary,
        skillsArray: form.skillsArray || [],
        locationsArray: form.locationsArray || [],
        educations: form.educations || [],
        isGoogle: isGoogleSignup,
        username: usernameInput,
      });

      /* Upload profile photo if selected */
      if (form.profilePhoto && data.token) {
        try {
          const photoForm = new FormData();
          photoForm.append("photo", form.profilePhoto);
          await axios.post("/api/candidate/upload-photo", photoForm, {
            headers: { Authorization: `Bearer ${data.token}`, "Content-Type": "multipart/form-data" },
          });
        } catch (photoErr) {
          console.warn("Photo upload failed (non-critical):", photoErr.message);
        }
      }

      localStorage.setItem("token", data.token || "");
      localStorage.setItem("user", JSON.stringify({
        id: data.candidateId, username: data.username,
        firstName: form.firstName, lastName: form.lastName,
        email: form.email, role: "candidate",
      }));
      window.dispatchEvent(new Event("auth:update"));

      setCreatedUsername(data.username || "");
      setSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.error || "Signup failed. Please try again.");
    }
  };

  /* FIX Bug 4: real Google signup using actual token */
  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleError("");
    try {
      /* Try login first — existing Google users go straight to their profile */
      const { data } = await axios.post("/api/login/google", { credential: credentialResponse.credential });
      if (data.token) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        window.dispatchEvent(new Event("auth:update"));
        navigate(data.user.role === "employer" ? "/employer/recruiter-dashboard" : "/employee/profile");
        return;
      }
    } catch { /* not an existing user — fall through to prefill */ }

    /* New user — decode the JWT payload to prefill form */
    try {
      const payload = JSON.parse(atob(credentialResponse.credential.split(".")[1]));
      setIsGoogleSignup(true);
      setForm(f => ({
        ...f,
        firstName: payload.given_name || "",
        lastName: payload.family_name || "",
        email: payload.email || "",
        /* Generate a valid dummy password — server hashes it, user never needs it */
        password: `Gg_${Math.random().toString(36).slice(2, 10)}1`,
      }));
      setExiting(true);
      setTimeout(() => { setStep(1); setExiting(false); }, 260);
    } catch {
      setGoogleError("Could not read Google account info. Please try again.");
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setForm({ countryIso: "IN", countryCode: "+91", educations: [{}], password: "" });
    setStep(0);
    setTouched({});
    setSubmitAttempted(false);
    setIsGoogleSignup(false);
    setUsernameInput("");
    setUsernameAvailable(null);
    setUsernameSuggestions([]);
    setGoogleError("");
  };

  /* FIX Bug 3: added errors.firstName / errors.lastName to deps */
  useEffect(() => {
    if (!form.firstName || !form.lastName) return;
    if (errors.firstName || errors.lastName) return;
    if (usernameInput) return;
    setUsernameInput(generateUsername(form.firstName, form.lastName));
  }, [form.firstName, form.lastName, errors.firstName, errors.lastName]);

  /* Debounced username uniqueness check */
  useEffect(() => {
    if (!usernameInput || usernameInput.length < 4) { setUsernameAvailable(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`/api/candidate/check-username?username=${encodeURIComponent(usernameInput)}`);
        setUsernameAvailable(!data.exists);
        if (data.exists) setUsernameSuggestions(generateUsernameSuggestions(form.firstName, form.lastName));
        else setUsernameSuggestions([]);
      } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [usernameInput]);

  /* Email duplicate check */
  useEffect(() => {
    if (!form.email || errors.email) { setEmailExists(false); return; }
    const t = setTimeout(async () => {
      try { const { data } = await axios.get(`/api/candidate/check-email?email=${encodeURIComponent(form.email)}`); setEmailExists(data.exists); } catch {}
    }, 600);
    return () => clearTimeout(t);
  }, [form.email]);

  /* Phone duplicate check */
  useEffect(() => {
    if (!form.mobile || errors.mobile) { setMobileExists(false); return; }
    const t = setTimeout(async () => {
      try { const { data } = await axios.get(`/api/candidate/check-phone?mobile=${encodeURIComponent(form.mobile)}`); setMobileExists(data.exists); } catch {}
    }, 600);
    return () => clearTimeout(t);
  }, [form.mobile]);

  if (submitted) return <SuccessScreen onReset={handleReset} createdUsername={createdUsername} />;

  const panels = [
    <StepAccount form={form} set={setField} errors={errors} touched={touched} touch={touch} isGoogleSignup={isGoogleSignup} emailExists={emailExists} usernameInput={usernameInput} setUsernameInput={setUsernameInput} usernameAvailable={usernameAvailable} usernameSuggestions={usernameSuggestions} />,
    <StepEducation form={form} set={setField} errors={errors} touched={touched} touch={touch} />,
    <StepPreferences form={form} set={setField} errors={errors} touched={touched} touch={touch} isGoogleSignup={isGoogleSignup} mobileExists={mobileExists} />,
  ];

  const isLast = step === STEPS.length - 1;
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="cs-root" style={{ marginTop: "80px" }}>
      <FloatingOrbs />
      {isGoogleSignup && (
        <div className="cs-google-banner">
          <span>🔗</span>
          <span>Signed up with Google — Please complete your profile to activate your account</span>
        </div>
      )}
      <div className="cs-layout">
        <aside className="cs-sidebar">
          <div className="cs-logo">
            <div className="cs-logo-mark"><img src={Image} alt="image" className="gif-icon" /></div>
            <div>
              <div className="cs-logo-name">TalentHive</div>
              <div className="cs-logo-sub">Candidate Profile</div>
            </div>
          </div>
          <StepBar current={step} />
          <div className="cs-benefits">
            {[{ icon: "🚀", text: "Build your profile & get noticed by recruiters" }, { icon: "🔔", text: "Get personalised job alerts instantly" }, { icon: "📈", text: "Apply easily and grow your career" }].map((b, i) => (
              <div key={i} className="cs-benefit-item" style={{ animationDelay: `${0.1 + i * 0.1}s` }}>
                <span className="cs-bi-icon">{b.icon}</span>
                <span>{b.text}</span>
              </div>
            ))}
          </div>
        </aside>

        <main className="cs-card" ref={cardRef}>
          <div className="cs-card-header">
            <div className="cs-ch-label">STEP {step + 1} / {STEPS.length}</div>
            <div className="cs-xp-track-sm"><div className="cs-xp-fill-sm" style={{ width: `${progress}%` }} /></div>
            <div className="cs-ch-pct">{Math.round(progress)}%</div>
          </div>

          <div className={`cs-panel-wrapper ${exiting ? "panel-exit" : "panel-enter"}`}>
            {panels[step]}
          </div>

          {/* Google signup temporarily disabled */}

          <div className="cs-card-nav">
            {step > 0 ? <button className="cs-btn-back" onClick={() => transition(-1)}>← Back</button> : <div />}
            {isLast ? (
              <button className={`cs-btn-primary ${submitAttempted && !step2Valid ? "shake" : ""}`} onClick={handleSubmit}>
                Create Account <span className="cs-btn-arrow">→</span>
              </button>
            ) : (
              <button className={`cs-btn-primary ${step === 0 && submitAttempted && !step0Valid ? "shake" : ""}`} onClick={() => transition(1)}>
                Continue <span className="cs-btn-arrow">→</span>
              </button>
            )}
          </div>

          {/* FIX Bug 7: navigate to home instead of broken href="#" */}
          <p className="cs-signin-prompt">
            Already have an account?{" "}
            <a href="/" className="cs-signin-link" onClick={e => { e.preventDefault(); navigate("/"); }}>
              Sign in here
            </a>
          </p>
        </main>
      </div>
    </div>
  );
}
